import { useState } from "react";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { offersApi } from "../lib/offersApi";

/**
 * Top-of-workbench banner shown only when workflow_status ===
 * "in_attesa_approvazione" and the current user has approve/reject rights.
 */
export default function ApprovalBanner({ session, onRefresh }) {
  const [mode, setMode] = useState(null); // null | "approve" | "reject"
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  if (!session?.session_id || session?.workflow_status !== "in_attesa_approvazione") {
    return null;
  }

  const approve = async () => {
    setBusy(true);
    try {
      await offersApi.approve(session.session_id, notes || "");
      toast.success("Offerta approvata");
      setMode(null);
      setNotes("");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore approvazione");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    if (!(notes || "").trim()) {
      toast.error("Inserisci una motivazione per la modifica richiesta");
      return;
    }
    setBusy(true);
    try {
      await offersApi.reject(session.session_id, notes);
      toast.success("Offerta rimandata al commerciale");
      setMode(null);
      setNotes("");
      onRefresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mx-4 mt-3 mb-1 rounded-md p-3"
      style={{
        background: "#FFFBEB",
        border: "1px solid #FDE68A",
      }}
      data-testid="approval-banner"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" style={{ color: "#B45309" }} />
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold" style={{ color: "#92400E" }}>
            Questa offerta richiede la tua approvazione
          </div>
          <div className="text-[11px] mt-0.5" style={{ color: "#92400E" }}>
            Valore {Number(session.value_total || 0).toFixed(2)} € supera la
            soglia 3.000 €. Verifica margine e spedizione prima di procedere.
          </div>
        </div>
        {mode === null && (
          <div className="flex items-center gap-1.5 shrink-0">
            <button
              type="button"
              onClick={() => setMode("approve")}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] font-medium"
              style={{ background: "#16A34A", color: "white" }}
              data-testid="approve-open-btn"
              disabled={busy}
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Approva
            </button>
            <button
              type="button"
              onClick={() => setMode("reject")}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-md text-[12px] font-medium"
              style={{
                background: "white",
                color: "#DC2626",
                border: "1px solid #FEE2E2",
              }}
              data-testid="reject-open-btn"
              disabled={busy}
            >
              <X className="h-3.5 w-3.5" />
              Rifiuta e chiedi modifiche
            </button>
          </div>
        )}
      </div>
      {mode !== null && (
        <div className="mt-2.5 flex items-start gap-2">
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            placeholder={
              mode === "approve"
                ? "Note approvazione (opzionali)"
                : "Motivazione modifiche richieste (obbligatoria)"
            }
            className="flex-1 rounded text-[12px] p-2"
            style={{ border: "1px solid var(--border-default)", background: "white" }}
            data-testid={`${mode}-notes`}
            autoFocus
          />
          <div className="flex flex-col gap-1 shrink-0">
            <button
              type="button"
              onClick={mode === "approve" ? approve : reject}
              disabled={busy}
              className="h-8 px-3 rounded-md text-[12px] font-medium disabled:opacity-60"
              style={{
                background: mode === "approve" ? "#16A34A" : "#DC2626",
                color: "white",
              }}
              data-testid={`${mode}-submit`}
            >
              {busy ? "…" : mode === "approve" ? "Conferma" : "Rifiuta"}
            </button>
            <button
              type="button"
              onClick={() => {
                setMode(null);
                setNotes("");
              }}
              className="h-7 px-2 rounded text-[11px]"
              style={{ color: "var(--text-muted)" }}
              data-testid={`${mode}-cancel`}
            >
              Annulla
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
